import { useCallback, useEffect, useState } from "react";
import { listSavedKits, saveKit, unsaveKit } from "@/lib/api";

// Tiny shared cache of the user's saved kit_keys, mirroring the
// useSavedChannels pattern. Every Save button reads from this so toggle
// state stays in sync across pages without each row firing its own API.
const subscribers = new Set();
let cached = null; // Set<string> | null
let inFlight = null;

async function ensureLoaded() {
  if (cached) return cached;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const rows = await listSavedKits();
      cached = new Set(rows.map((r) => r.kit_key));
    } catch {
      cached = new Set();
    }
    inFlight = null;
    return cached;
  })();
  return inFlight;
}

function notify() {
  subscribers.forEach((fn) => fn(new Set(cached || [])));
}

export function useSavedKitKeys() {
  const [keys, setKeys] = useState(cached ? new Set(cached) : new Set());

  useEffect(() => {
    let mounted = true;
    ensureLoaded().then((s) => {
      if (mounted) setKeys(new Set(s));
    });
    subscribers.add(setKeys);
    return () => {
      mounted = false;
      subscribers.delete(setKeys);
    };
  }, []);

  const toggle = useCallback(async (payload) => {
    await ensureLoaded();
    const k = payload.kit_key;
    const isSaved = cached.has(k);
    if (isSaved) {
      cached.delete(k);
      notify();
      try {
        await unsaveKit(k);
      } catch (err) {
        cached.add(k);
        notify();
        throw err;
      }
    } else {
      cached.add(k);
      notify();
      try {
        await saveKit(payload);
      } catch (err) {
        cached.delete(k);
        notify();
        throw err;
      }
    }
    return !isSaved;
  }, []);

  return { keys, toggle };
}

export function invalidateSavedKits() {
  cached = null;
  inFlight = null;
}
